(() => {
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  function initReveal(scope = document) {
    const items = $$('.ix-reveal:not(.is-visible)', scope);
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -35px' });
    items.forEach((item) => observer.observe(item));
  }

  function initMenu() {
    const button = $('[data-menu-button]');
    const menu = $('[data-mobile-menu]');
    if (!button || !menu || button.dataset.menuBound) return;
    button.dataset.menuBound = 'true';
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      menu.hidden = !open;
      document.body.classList.toggle('menu-open', open);
    });
    $$('a', menu).forEach((link) => link.addEventListener('click', () => {
      button.setAttribute('aria-expanded', 'false');
      menu.hidden = true;
      document.body.classList.remove('menu-open');
    }));
  }

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(Number(cents) / 100);
    } catch (_) {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }
  }

  function updateVariantUI(select) {
    const option = select.options[select.selectedIndex];
    if (!option) return;
    const root = select.closest('[data-product-root], [data-section-id]') || document;
    const priceValue = Number(option.dataset.price || 0);
    const compareValue = Number(option.dataset.compare || 0);
    const available = option.dataset.available !== 'false' && !option.disabled;
    const priceText = formatMoney(priceValue);

    $$('[data-price], [data-button-price], [data-offer-price], [data-mobile-price]', root).forEach((node) => { node.textContent = priceText; });
    $$('[data-compare-price], [data-offer-compare]', root).forEach((node) => {
      node.textContent = compareValue > priceValue ? formatMoney(compareValue) : '';
      node.hidden = compareValue <= priceValue;
    });
    const save = $('[data-save-badge]', root);
    if (save && compareValue > priceValue) save.textContent = `SAVE ${formatMoney(compareValue - priceValue)}`;

    const atc = $('[data-atc-button]', root);
    const atcLabel = $('[data-atc-label]', root);
    if (atc) atc.disabled = !available;
    if (atcLabel) atcLabel.textContent = available ? 'ADD TO BAG' : 'SOLD OUT';

    const stockCard = $('[data-stock-card]', root);
    const stockLabel = $('[data-stock-label]', root);
    if (stockCard && stockLabel) {
      const managed = option.dataset.inventoryManaged === 'true';
      const inventory = Number(option.dataset.inventory || 0);
      const low = Number(stockCard.dataset.lowStock || 9);
      if (!available) stockLabel.textContent = 'CURRENTLY SOLD OUT';
      else if (managed && inventory > 0 && inventory <= low) stockLabel.textContent = `LOW STOCK — ${inventory} READY TO SHIP`;
      else stockLabel.textContent = 'IN STOCK — READY TO SHIP';
    }
  }

  function initVariants(scope = document) {
    $$('[data-variant-select]:not([data-variant-bound])', scope).forEach((select) => {
      select.dataset.variantBound = 'true';
      select.addEventListener('change', () => updateVariantUI(select));
    });
  }

  function initProductOptions(scope = document) {
    $$('[data-product-root]:not([data-options-bound])', scope).forEach((root) => {
      root.dataset.optionsBound = 'true';
      const select = $('[data-variant-select]', root);
      const jsonNode = $('[data-product-variants]', root);
      const groups = $$('.ix-option-group', root);
      if (!select || !jsonNode || !groups.length) return;
      let variants;
      try { variants = JSON.parse(jsonNode.textContent); } catch (_) { return; }

      groups.forEach((group) => {
        $$('.ix-option-pill', group).forEach((button) => {
          button.addEventListener('click', () => {
            $$('.ix-option-pill', group).forEach((item) => item.classList.remove('is-active'));
            button.classList.add('is-active');
            const label = $('[data-option-selected]', group);
            if (label) label.textContent = button.dataset.optionValue;

            const values = groups.map((item) => $('.ix-option-pill.is-active', item)?.dataset.optionValue || '');
            const variant = variants.find((item) => item.options.every((value, index) => value === values[index]));
            if (!variant) return;
            select.value = String(variant.id);
            select.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      });
    });
  }

  function initProductGallery(scope = document) {
    $$('[data-product-gallery]:not([data-gallery-bound])', scope).forEach((gallery) => {
      gallery.dataset.galleryBound = 'true';
      const slides = $$('[data-gallery-slide]', gallery);
      const thumbs = $$('[data-gallery-thumb]', gallery);
      const count = $('[data-gallery-current]', gallery);
      let active = 0;
      const show = (index) => {
        if (!slides.length) return;
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === active));
        thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === active);
          thumb.setAttribute('aria-current', i === active ? 'true' : 'false');
        });
        thumbs[active]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        if (count) count.textContent = String(active + 1);
      };
      thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => show(index)));
      $('[data-gallery-prev]', gallery)?.addEventListener('click', () => show(active - 1));
      $('[data-gallery-next]', gallery)?.addEventListener('click', () => show(active + 1));
      let startX = null;
      gallery.addEventListener('touchstart', (event) => { startX = event.touches[0].clientX; }, { passive: true });
      gallery.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const delta = event.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 45) show(active + (delta < 0 ? 1 : -1));
        startX = null;
      }, { passive: true });
      show(0);
    });
  }

  function initReviews(scope = document) {
    $$('[data-review-track]:not([data-review-bound])', scope).forEach((track) => {
      track.dataset.reviewBound = 'true';
      const wrap = track.parentElement;
      const prev = $('[data-review-prev]', wrap);
      const next = $('[data-review-next]', wrap);
      const jump = () => Math.max(track.clientWidth * 0.82, 280);
      prev?.addEventListener('click', () => track.scrollBy({ left: -jump(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: jump(), behavior: 'smooth' }));
    });
  }

  function initAccordions(scope = document) {
    $$('.ix-accordion details:not([data-accordion-bound]), .ix-pdp-accordions details:not([data-accordion-bound])', scope).forEach((detail) => {
      detail.dataset.accordionBound = 'true';
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        const container = detail.parentElement;
        $$('details', container).forEach((other) => { if (other !== detail) other.removeAttribute('open'); });
      });
    });
  }

  function initCompatibilityGuide(scope = document) {
    $$('[data-compatibility-guide]:not([data-compatibility-bound])', scope).forEach((guide) => {
      guide.dataset.compatibilityBound = 'true';
      const tabs = $$('[data-compat-tab]', guide);
      const panels = $$('[data-compat-panel]', guide);
      function activateTab(key) {
        tabs.forEach((tab) => { const active = tab.dataset.compatTab === key; tab.classList.toggle('is-active', active); tab.setAttribute('aria-selected', String(active)); });
        panels.forEach((panel) => { const active = panel.dataset.compatPanel === key; panel.classList.toggle('is-active', active); panel.hidden = !active; });
      }
      tabs.forEach((tab) => tab.addEventListener('click', () => activateTab(tab.dataset.compatTab)));
      $$('[data-compat-apply]', guide).forEach((button) => button.addEventListener('click', () => {
        const connector = button.dataset.compatApply;
        const productRoot = document.querySelector('[data-product-root]');
        const activeBundle = productRoot?.querySelector('.ix-bundle-option.is-active');
        const bundleSelects = activeBundle ? $$('[data-bundle-port]', activeBundle) : [];
        if (bundleSelects.length) {
          bundleSelects.forEach((select) => { select.value = connector; select.dispatchEvent(new Event('change', { bubbles: true })); });
        } else {
          const optionButton = $$('[data-option-value]', productRoot || document).find((item) => item.dataset.optionValue === connector);
          optionButton?.click();
        }
        (productRoot?.querySelector('[data-bundle-picker]') || productRoot)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }));
      activateTab(tabs.find((tab) => tab.classList.contains('is-active'))?.dataset.compatTab || 'usbc');
    });
  }

  function initMobileAtc(scope = document) {
    $$('[data-mobile-atc-button]:not([data-mobile-bound])', scope).forEach((button) => {
      button.dataset.mobileBound = 'true';
      button.addEventListener('click', () => {
        const root = document.querySelector('[data-product-root]');
        const atc = root?.querySelector('[data-atc-button]');
        if (atc && !atc.disabled) atc.click();
        else root?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function initBundlePicker(scope = document) {
    $$('[data-bundle-picker]:not([data-bundle-bound])', scope).forEach((picker) => {
      picker.dataset.bundleBound = 'true';
      const root = picker.closest('[data-product-root]');
      const form = root?.querySelector('[data-product-form]');
      const variantSelect = root?.querySelector('[data-variant-select]');
      const variantsNode = root?.querySelector('[data-product-variants]');
      const options = $$('.ix-bundle-option', picker);
      const atc = root?.querySelector('[data-atc-button]');
      const atcLabel = root?.querySelector('[data-atc-label]');
      const quantityInput = root?.querySelector('[data-bundle-quantity]');
      let selectedBundle = 1;
      let variants = [];
      try { variants = variantsNode ? JSON.parse(variantsNode.textContent) : []; } catch (_) { variants = []; }

      const normalized = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9.]/g, '');
      const variantForPort = (port) => variants.find((variant) => variant.available && variant.options.some((value) => normalized(value) === normalized(port)));
      const moneyFromDecimal = (value) => formatMoney(Math.round(Number(value)));

      function syncSingleVariant() {
        if (selectedBundle !== 1 || !variantSelect) return;
        const port = $('.ix-bundle-option.is-active [data-bundle-port]', picker)?.value;
        const variant = variantForPort(port);
        if (variant) {
          variantSelect.value = String(variant.id);
          variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      function activate(option) {
        options.forEach((item) => {
          item.classList.toggle('is-active', item === option);
          const config = $('.ix-bundle-config', item);
          if (config) config.hidden = item !== option;
        });
        selectedBundle = Number(option.dataset.bundleOption || 1);
        if (quantityInput) quantityInput.value = String(selectedBundle);
        const priceText = moneyFromDecimal(option.dataset.bundlePrice);
        const compareText = moneyFromDecimal(option.dataset.bundleCompare);
        $$('[data-button-price], [data-mobile-price], [data-price]', root).forEach((node) => { node.textContent = priceText; });
        $$('[data-compare-price]', root).forEach((node) => { node.hidden = false; node.textContent = compareText; });
        const save = $('[data-save-badge]', root);
        if (save) save.textContent = `SAVE ${moneyFromDecimal(Number(option.dataset.bundleCompare) - Number(option.dataset.bundlePrice))}`;
        if (atcLabel) atcLabel.textContent = selectedBundle === 1 ? 'ADD TO BAG' : `ADD ${selectedBundle}-PAIR BUNDLE`;
        syncSingleVariant();
      }

      options.forEach((option) => {
        $('.ix-bundle-choice', option)?.addEventListener('click', () => activate(option));
        $$('[data-bundle-port]', option).forEach((select) => select.addEventListener('change', syncSingleVariant));
      });
      activate($('.ix-bundle-option.is-active', picker) || options[0]);

      form?.addEventListener('submit', async (event) => {
        event.preventDefault();
        const active = $('.ix-bundle-option.is-active', picker);
        const ports = $$('[data-bundle-port]', active).map((select) => select.value);
        if (!ports.length || !variants.length) {
          alert('Bundle variants are not configured yet. Add USB-C, Lightning, and 3.5mm product variants in Shopify.');
          return;
        }
        const selectedVariants = ports.map(variantForPort);
        if (selectedVariants.some((variant) => !variant)) {
          alert('One or more connector variants could not be found. Check the product variant names in Shopify.');
          return;
        }
        if (atc) atc.disabled = true;
        if (atcLabel) atcLabel.textContent = 'ADDING…';
        const items = selectedVariants.map((variant, index) => ({
          id: variant.id,
          quantity: 1,
          properties: { 'Bundle': `Buy ${selectedBundle}`, 'Pair': String(index + 1), 'Selected port': ports[index] }
        }));
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items })
          });
          if (!response.ok) throw new Error('Unable to add bundle');
          const code = selectedBundle === 2 ? picker.dataset.codeTwo : selectedBundle === 3 ? picker.dataset.codeThree : '';
          if (code) {
            try { await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`, { credentials: 'same-origin' }); } catch (_) {}
          }
          if (atc) atc.disabled = false;
          if (atcLabel) atcLabel.textContent = selectedBundle === 1 ? 'ADD TO BAG' : `ADD ${selectedBundle}-PAIR BUNDLE`;
          window.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        } catch (error) {
          alert('The bundle could not be added. Please check the Shopify variants and discount setup.');
          if (atc) atc.disabled = false;
          if (atcLabel) atcLabel.textContent = selectedBundle === 1 ? 'ADD TO BAG' : `ADD ${selectedBundle}-PAIR BUNDLE`;
        }
      });
    });
  }

  function initLocalization(scope = document) {
    $$('[data-localization-select]:not([data-localization-bound])', scope).forEach((select) => {
      select.dataset.localizationBound = 'true';
      select.addEventListener('change', () => select.closest('form')?.submit());
    });
  }

  function initLiveCartDrawer(scope = document) {
    const drawer = $('[data-cart-drawer]');
    if (!drawer || drawer.dataset.drawerBound) return;
    drawer.dataset.drawerBound = 'true';
    const overlay = $('[data-cart-drawer-overlay]');
    const itemsNode = $('[data-cart-drawer-items]', drawer);
    const emptyNode = $('[data-cart-drawer-empty]', drawer);
    const footNode = $('[data-cart-drawer-foot]', drawer);
    const statusNode = $('[data-cart-drawer-status]', drawer);
    const subtotalNode = $('[data-cart-drawer-subtotal]', drawer);
    const drawerCount = $('[data-drawer-count]', drawer);
    let currentCart = null;

    const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
    const activeCurrency = () => currentCart?.currency || window.Shopify?.currency?.active || 'USD';
    const shippingThreshold = () => {
      const currency = activeCurrency();
      const raw = currency === 'CAD' ? drawer.dataset.thresholdCad : currency === 'GBP' ? drawer.dataset.thresholdGbp : drawer.dataset.thresholdUsd;
      return Math.max(0, Math.round(Number(raw || 0) * 100));
    };

    function setStatus(message = '') { if (statusNode) statusNode.textContent = message; }
    function updateGlobalCount(count) {
      $$('[data-cart-count]').forEach((node) => { node.textContent = count; });
      if (drawerCount) drawerCount.textContent = count;
    }
    function updateShippingProgress(cart) {
      const message = $('[data-shipping-message]', drawer);
      const bar = $('[data-shipping-bar]', drawer);
      if (!message || !bar) return;
      if (drawer.dataset.freeShippingAll === 'true') { message.textContent = 'Free tracked shipping on every eligible order'; bar.style.width = '100%'; return; }
      const threshold = shippingThreshold();
      if (!threshold) { message.textContent = 'Tracked shipping calculated at checkout'; bar.style.width = '0%'; return; }
      const remaining = Math.max(0, threshold - cart.total_price);
      const progress = Math.min(100, (cart.total_price / threshold) * 100);
      bar.style.width = `${progress}%`;
      message.textContent = remaining > 0 ? `${formatMoney(remaining)} away from the shipping offer` : 'Shipping offer unlocked';
    }

    async function getCart() {
      const response = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error('Unable to load bag');
      return response.json();
    }
    async function changeQuantity(key, quantity) {
      setStatus('Updating bag…');
      const response = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      if (!response.ok) throw new Error('Unable to update item');
      await refresh();
    }
    async function changeVariant(item, newVariantId, newVariantTitle) {
      if (Number(newVariantId) === Number(item.variant_id)) return;
      setStatus('Changing connector…');
      const properties = { ...(item.properties || {}) };
      if (Object.prototype.hasOwnProperty.call(properties, 'Selected port')) properties['Selected port'] = newVariantTitle;
      const addResponse = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(newVariantId), quantity: item.quantity, properties }] }) });
      if (!addResponse.ok) throw new Error('Unable to add replacement variant');
      const removeResponse = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: item.key, quantity: 0 }) });
      if (!removeResponse.ok) throw new Error('Unable to remove original variant');
      await refresh();
    }

    async function loadVariantOptions(select, item) {
      try {
        const response = await fetch(`/products/${encodeURIComponent(item.handle)}.js`, { headers: { 'Accept': 'application/json' } });
        if (!response.ok) throw new Error('Product unavailable');
        const product = await response.json();
        select.innerHTML = '';
        product.variants.forEach((variant) => {
          const option = document.createElement('option');
          option.value = variant.id;
          option.textContent = `${variant.title}${variant.available ? '' : ' — Sold out'}`;
          option.disabled = !variant.available;
          option.selected = Number(variant.id) === Number(item.variant_id);
          option.dataset.title = variant.title;
          select.appendChild(option);
        });
        select.addEventListener('change', async () => {
          const option = select.options[select.selectedIndex];
          try { await changeVariant(item, select.value, option.dataset.title || option.textContent); }
          catch (error) { setStatus('Unable to change the connector. Please try again.'); select.value = String(item.variant_id); }
        });
      } catch (_) {
        select.disabled = true;
      }
    }

    function renderCart(cart) {
      currentCart = cart;
      updateGlobalCount(cart.item_count);
      updateShippingProgress(cart);
      if (subtotalNode) subtotalNode.textContent = formatMoney(cart.total_price);
      const empty = cart.item_count === 0;
      emptyNode.hidden = !empty;
      footNode.hidden = empty;
      itemsNode.innerHTML = '';
      if (empty) { setStatus(''); return; }
      cart.items.forEach((item, index) => {
        const line = document.createElement('article');
        line.className = 'ix-live-cart-item';
        const image = item.image || item.featured_image?.url || '';
        const variantText = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : 'Silver';
        line.innerHTML = `<a class="ix-live-cart-item__image" href="${escapeHtml(item.url)}">${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.product_title)}">` : ''}</a><div class="ix-live-cart-item__info"><a href="${escapeHtml(item.url)}"><strong>${escapeHtml(item.product_title)}</strong></a><small>${escapeHtml(variantText)}</small><label>Connector / type<select data-drawer-variant aria-label="Change connector for ${escapeHtml(item.product_title)}"><option value="${item.variant_id}">${escapeHtml(variantText)}</option></select></label><div class="ix-live-cart-item__actions"><div class="ix-live-cart-qty"><button type="button" data-drawer-minus aria-label="Decrease quantity">−</button><b>${item.quantity}</b><button type="button" data-drawer-plus aria-label="Increase quantity">+</button></div><button type="button" data-drawer-remove>Remove</button></div></div><strong class="ix-live-cart-item__price">${formatMoney(item.final_line_price)}</strong>`;
        itemsNode.appendChild(line);
        const select = $('[data-drawer-variant]', line);
        loadVariantOptions(select, item);
        $('[data-drawer-minus]', line).addEventListener('click', () => changeQuantity(item.key, Math.max(0, item.quantity - 1)).catch(() => setStatus('Unable to update the bag.')));
        $('[data-drawer-plus]', line).addEventListener('click', () => changeQuantity(item.key, item.quantity + 1).catch(() => setStatus('Unable to update the bag.')));
        $('[data-drawer-remove]', line).addEventListener('click', () => changeQuantity(item.key, 0).catch(() => setStatus('Unable to remove the item.')));
      });
      setStatus('');
    }

    async function refresh() {
      try { setStatus('Loading bag…'); renderCart(await getCart()); }
      catch (_) { setStatus('Unable to load the bag. Open the full cart page instead.'); }
    }
    function openDrawer(event) {
      event?.preventDefault();
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      overlay.hidden = false;
      document.body.classList.add('drawer-open');
      refresh();
      setTimeout(() => $('[data-cart-drawer-close]', drawer)?.focus(), 260);
    }
    function closeDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      overlay.hidden = true;
      document.body.classList.remove('drawer-open');
    }

    $$('[data-cart-drawer-open]').forEach((button) => {
      if (button.dataset.drawerOpenBound) return;
      button.dataset.drawerOpenBound = 'true';
      button.addEventListener('click', openDrawer);
    });
    $('[data-cart-drawer-close]', drawer)?.addEventListener('click', closeDrawer);
    $('[data-cart-drawer-close-link]', drawer)?.addEventListener('click', closeDrawer);
    overlay?.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer(); });
    window.addEventListener('cart:refresh', (event) => { if (event.detail?.open) openDrawer(); else refresh(); });
  }

  function initCartControls(scope = document) {
    $$('[data-cart-line]:not([data-cart-bound])', scope).forEach((line) => {
      line.dataset.cartBound = 'true';
      const key = line.dataset.lineKey;
      const quantity = Number(line.dataset.lineQuantity || 1);
      const message = $('[data-cart-line-message]', line);
      const propertiesNode = $('[data-line-properties]', line);
      let properties = {};
      try { properties = propertiesNode ? JSON.parse(propertiesNode.textContent) : {}; } catch (_) { properties = {}; }

      $('[data-cart-remove]', line)?.addEventListener('click', async () => {
        if (message) message.textContent = 'Removing…';
        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
          if (!response.ok) throw new Error('Remove failed');
          window.location.reload();
        } catch (_) {
          if (message) message.textContent = 'Unable to remove. Please try again.';
        }
      });

      $('[data-cart-variant]', line)?.addEventListener('change', async (event) => {
        const select = event.currentTarget;
        const newVariantId = Number(select.value);
        const newTitle = select.options[select.selectedIndex]?.dataset.title || select.options[select.selectedIndex]?.textContent || '';
        if (message) message.textContent = 'Changing type…';
        const updatedProperties = { ...properties };
        if (Object.prototype.hasOwnProperty.call(updatedProperties, 'Selected port')) updatedProperties['Selected port'] = newTitle;
        try {
          const addResponse = await fetch('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: newVariantId, quantity, properties: updatedProperties }] })
          });
          if (!addResponse.ok) throw new Error('Add replacement failed');
          const removeResponse = await fetch('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
          if (!removeResponse.ok) throw new Error('Remove original failed');
          window.location.reload();
        } catch (_) {
          if (message) message.textContent = 'Unable to change the type. Please refresh and try again.';
        }
      });
    });
  }

  function init(scope = document) {
    initReveal(scope);
    initMenu();
    initLocalization(scope);
    initLiveCartDrawer(scope);
    initVariants(scope);
    initProductOptions(scope);
    initProductGallery(scope);
    initReviews(scope);
    initAccordions(scope);
    initBundlePicker(scope);
    initCompatibilityGuide(scope);
    initCartControls(scope);
    initMobileAtc(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
